import { useState, useId } from "react";
import { labelClass } from "./fieldStyles";

interface InlineToggleProps {
  label: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
  /** Error message to show when touched and invalid. Null = valid. */
  error?: string | null;
  /** When > 0, marks the field as touched (used by parent "touch all" triggers). */
  touchAllCounter?: number;
  required?: boolean;
  /** When true, both buttons are unclickable and non-interactive. Use with a fixed `value` to render a locked answer. */
  disabled?: boolean;
  containerClassName?: string;
  /** Small explanatory line rendered under the radio group and linked via aria-describedby
   *  so screen-reader users hear the explanation when the group receives focus. Used to
   *  explain why a disabled toggle is locked to a specific answer. */
  hint?: string;
}

/**
 * Controlled Yes/No toggle with built-in touch state and error display.
 * Use for array item fields (owners, groups) where the store-bound
 * ToggleInput doesn't apply.
 *
 * Usage:
 *   <InlineToggle label="Is W-2 employee?" value={row.receivingW2Wages}
 *     onChange={(v) => update({ receivingW2Wages: v })}
 *     error={row.receivingW2Wages === null ? "Required" : null} required />
 */
export function InlineToggle({
  label,
  value,
  onChange,
  error,
  touchAllCounter = 0,
  required,
  disabled,
  containerClassName,
  hint,
}: InlineToggleProps) {
  const labelId = useId();
  const errorId = `${labelId}-error`;
  const hintId = `${labelId}-hint`;
  const [internallyTouched, setInternallyTouched] = useState(false);
  // Touched if the user interacted OR the parent triggered a touch-all sweep.
  const touched = internallyTouched || touchAllCounter > 0;

  const showError = disabled ? null : (touched ? error : null);
  // Chain describedby ids so the radiogroup announces both the error (if visible) and the hint.
  const describedBy = [showError ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined;

  const base = "flex-1 py-2.5 text-sm font-medium rounded-lg border transition-colors text-center";
  const clickable = disabled ? "cursor-not-allowed" : "cursor-pointer";
  // Keep the green "selected" indication even when disabled so a forced answer
  // still reads as an answer, not as a greyed-out placeholder.
  const activeClasses = "bg-brand-green-10 text-brand-green border-brand-green font-semibold";
  const inactiveClasses = disabled
    ? "bg-gray-50 text-gray-300 border-gray-200"
    : showError
      ? "bg-danger-bg text-gray-600 border-danger"
      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50";

  const clickIfEnabled = (v: boolean) => {
    if (disabled) return;
    onChange(v);
    setInternallyTouched(true);
  };

  return (
    <div className={containerClassName}>
      <span id={labelId} className={labelClass()}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </span>
      <div className="flex gap-2 mt-1" role="radiogroup" aria-labelledby={labelId} aria-describedby={describedBy} onBlur={() => !disabled && setInternallyTouched(true)}>
        <button
          type="button"
          role="radio"
          aria-checked={value === true}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          className={`${base} ${clickable} ${value === true ? activeClasses : inactiveClasses} focus:outline-none focus:ring-2 focus:ring-brand-green-50`}
          onClick={() => clickIfEnabled(true)}
        >
          Yes
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={value === false}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          className={`${base} ${clickable} ${value === false ? activeClasses : inactiveClasses} focus:outline-none focus:ring-2 focus:ring-brand-green-50`}
          onClick={() => clickIfEnabled(false)}
        >
          No
        </button>
      </div>
      {showError && <p id={errorId} className="text-xs text-danger mt-1">{showError}</p>}
      {hint && <p id={hintId} className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}
